import React, { useState, useEffect } from 'react';
import { useDispatch } from "react-redux";
import { NotificationConstants } from './constants';
import CheckBox from '../../../components/Checkbox';
import { updateNotificationSettings } from '../actions/action';
import { mapUiDiffToApi } from './notificationSettingsMapper';
import { hasClass, addClass, removeClass } from '../../../common/util';
import Icon from '../../../icons';
import { useModules } from '../../../utils/modules';

const ManageNotifications = ({ data, error, uView, isLoading}) => {
    const [isAllAU, setIsAllAU] = useState(false);
    const [isAllEmail, setIsAllEmail] = useState(false);
    const [auSettings, setAuSettings] = useState(data);
    const dispatch = useDispatch();
    const { isModuleEnabled, moduleLabel } = useModules();

    // sync local state once the notification settings finish loading (or change externally)
    useEffect(() => {
        setAuSettings(data);
    }, [data]);

    // whenever auSettings object is updated, check for all checked value
    useEffect(() => {
        let checkAllAu = true, checkAllEmail = true;
        let newAuSettings = {...auSettings};
        delete newAuSettings.CanRecieveEmailNotificationManageUsersAddedNewUser;
        delete newAuSettings.CanRecieveAUManageUsersAddedNewUser;
        for(let key in newAuSettings) {
            if(checkAllAu && key.indexOf('AU') !== -1 && newAuSettings[key] == false) { // if we find any 'AU' key with value false
                checkAllAu = false // set allAu to false
            }
            if(checkAllEmail && key.indexOf('EmailNotification') !== -1 && newAuSettings[key] == false) {  // if we find any 'Email' key with value false
                checkAllEmail = false // set allEmail to false
            }
        }

        // update values in state
        setIsAllAU(checkAllAu);
        setIsAllEmail(checkAllEmail);
    }, [auSettings]);

    const handleCheck = (e) => {
        const type = e.value, value = !e.checked;
        let obj = Object.assign({}, auSettings);
        let diff = {}; // only the keys that actually changed - sent as a partial PUT

        // delete unused keys
        delete obj.CanRecieveEmailNotificationManageUsersAddedNewUser;
        delete obj.CanRecieveAUManageUsersAddedNewUser;

        if(type === 'CanReceiveAllEmailNotification'){
            for(let key in obj){
                if(key.indexOf('EmailNotification') !== -1 && obj[key] !== value){  // for all the keys containing EmailNotification with value not equal to updated value
                    obj[key] = value; // set value of that key to updated value
                    diff[key] = value;
                }
            }
        } else if(type === 'CanReceiveAllAU') {
            for(let key in obj) {
                if(key.indexOf('AU') !== -1 && obj[key] !== value){
                    obj[key] = value;
                    diff[key] = value;
                }
            }
        } else {
            obj[type] = value; // for a single key change
            diff[type] = value;
        }

        // set updated settings object in state
        setAuSettings(obj)

        // API call - send only the changed fields
        if (Object.keys(diff).length > 0) {
            dispatch(updateNotificationSettings(mapUiDiffToApi(diff)));
        }
    }

    const renderItem = (forEmail, forAU, moduleKey) => {
        const label = moduleKey
            ? moduleLabel(moduleKey, NotificationConstants[forAU])
            : NotificationConstants[forAU];
        return (
            <div className="rowSpaceBetween bottomBorder">
                <div className="action_list wth-10" dangerouslySetInnerHTML={{__html: label}}></div>
                <div className="rowSpaceAround wth-2">
                    <div className="checkboxArea" data-test={forEmail}>
                        <CheckBox disabled={isLoading} handleCheckbox={handleCheck} item={{id:forEmail,checked:auSettings[forEmail], value:forEmail}} />
                    </div>
                    <div className="checkboxArea" data-test={forAU}>
                        <CheckBox disabled={isLoading} handleCheckbox={handleCheck} item={{id:forAU, checked:auSettings[forAU],value:forAU}} />
                    </div>
                </div>
            </div>
        )
    }

    const handleScroll = (e) => {
        const scrollPos = document.querySelector("#editNotificationSetting").scrollTop
        const header = document.querySelector(".notificatonField")
        if(scrollPos !== 0) {
            if(!hasClass(header, "shadow")){
                addClass(header, "shadow")
            }
        } else {
            if (hasClass(header, "shadow")) {
                removeClass(header, "shadow")
            }
        }
    }

    if (error) {
        return (
            <div className="manageField">
                <strong>{error}</strong>
            </div>
        );
    }

    return (
        <React.Fragment>
            {/* @if (ViewBag.UserId == ViewBag.CurrentUserId) */}
            <div className="notificatonField alignMiddle">
                <div className="wth-10"><strong className="flexGrow"> Notification Settings </strong></div>
                <li className="rowSpaceAround wth-2 mobileCheck">
                    <div className="checkboxArea"><CheckBox disabled={isLoading} handleCheckbox={handleCheck} item={{checked:isAllEmail,id:"CheckAllEmailNotifications",value:"CanReceiveAllEmailNotification", label:<span class="labelText">Email Notifications</span> }}/><Icon name="email" className="largeView"/></div>
                    <div className="checkboxArea"><CheckBox disabled={isLoading} handleCheckbox={handleCheck} item={{checked:isAllAU, id:"CheckAllAUs", value:"CanReceiveAllAU", label:<span className="labelText">Notifications Panel</span> }} /><Icon name="bellNotification" className="largeView"/></div>
                </li>
            </div>
            <div className="manageField scrollArea" id="editNotificationSetting" onScroll={handleScroll}>
                {uView?.permissions?.can_accept_reject_b2b_relationship_request &&
                    <>
                        <strong>
                            Administration - Manage Relationships
                        </strong>
                        {renderItem('CanRecieveEmailNotificationManageRelationships', 'CanRecieveAUManageRelationships')}
                    </>
                }
                {uView?.permissions?.can_manage_contracts && isModuleEnabled('manage_profile.notification_setting.manage_contracts') &&
                    <>
                        <strong>
                            Administration - Manage Contracts
                        </strong>
                        {renderItem('CanRecieveEmailNotificationManageContracts', 'CanRecieveAUManageContracts')}
                    </>
                }
                <strong> Administration - Manage Users </strong>

                {uView?.permissions?.can_receive_un_assigned_workkards &&
                    renderItem('CanRecieveEmailNotificationManageUsersUnassignedWorkkards', 'CanRecieveAUManageUsersUnassignedWorkkards')
                }

                {uView?.permissions?.can_receive_un_assigned_activities &&
                    renderItem('CanRecieveEmailNotificationManageUsersUnassignedActivities', 'CanRecieveAUManageUsersUnassignedActivities')
                }

                {(uView?.canAddUser || uView?.canEditUser) &&
                    renderItem('CanRecieveEmailNotificationRequestToJoinOrg', 'CanRecieveAURequestToJoinOrg')
                }

                {(uView?.canAddUser || uView?.canEditUser) &&
                    renderItem('CanRecieveEmailNotificationWhenUserAdded', 'CanRecieveAURequestWhenUserAdded')
                }
                <strong> Workkard Notifications </strong>
                {renderItem('CanRecieveEmailNotificationWorkkardNoteAdded', 'CanRecieveAUWorkkardNoteAdded', 'manage_profile.notification_setting.workkard_note_added')}

                {renderItem('CanRecieveEmailNotificationWorkkardNoteWithFileOnlyAdded', 'CanRecieveAUWorkkardNoteWithFileOnlyAdded', 'manage_profile.notification_setting.workkard_note_with_file')}

                {renderItem('CanRecieveEmailNotificationWorkkardEdit', 'CanRecieveAUWorkkardEdit', 'manage_profile.notification_setting.workkard_edit')}

                {renderItem('CanRecieveEmailNotificationYouAreSponsorWorkkard', 'CanRecieveAUYouAreSponsorWorkkard')}

                {renderItem('CanRecieveEmailNotificationChangeSponsor', 'CanRecieveAUChangeSponsor', 'manage_profile.notification_setting.workkard_change_sponsor')}

                {process.env.foo_close_workkard === 'true' &&
                    renderItem('CanRecieveEmailNotificationWorkkardClose', 'CanRecieveAUWorkkardClose', 'manage_profile.notification_setting.workkard_close')
                }

                {isModuleEnabled('manage_profile.notification_setting.workkard_watcher') &&
                    renderItem('CanRecieveEmailNotificationWorkkardWatcher', 'CanRecieveAUWorkkardWatcher')
                }
                <strong> Activity Notifications </strong>
                {renderItem('CanRecieveEmailNotificationActivityAcceptedOrRejected', 'CanRecieveAUActivityAcceptedOrRejected', 'manage_profile.notification_setting.activity_accepted_or_rejected')}

                {renderItem('CanRecieveEmailNotificationActivityNoteAdded', 'CanRecieveAUActivityNoteAdded', 'manage_profile.notification_setting.activity_note_added')}

                {renderItem('CanRecieveEmailNotificationActivityNoteWithFileOnlyAdded', 'CanRecieveAUActivityNoteWithFileOnlyAdded', 'manage_profile.notification_setting.activity_note_with_file')}

                {renderItem('CanRecieveEmailNotificationActivityStatusChange', 'CanRecieveAUActivityStatusChange', 'manage_profile.notification_setting.activity_status_change')}

                {renderItem('CanRecieveEmailNotificationActivityCompleted', 'CanRecieveAUActivityCompleted', 'manage_profile.notification_setting.activity_completed')}

                {renderItem('CanRecieveEmailNotificationActivityClosed', 'CanRecieveAUActivityClosed', 'manage_profile.notification_setting.activity_closed')}

                {renderItem('CanRecieveEmailNotificationActivityReopened', 'CanRecieveAUActivityReopened', 'manage_profile.notification_setting.activity_reopened')}

                {renderItem('CanRecieveEmailNotificationActivityEdit', 'CanRecieveAUActivityEdit', 'manage_profile.notification_setting.activity_edit')}

                {renderItem('CanRecieveEmailNotificationActivityOwner', 'CanRecieveAUActivityOwner')}

                {renderItem('CanRecieveEmailNotificationNewOwnerActivity', 'CanRecieveAUNewOwnerActivity', 'manage_profile.notification_setting.activity_new_owner')}

                {renderItem('CanRecieveEmailNotificationActivityAssignee', 'CanRecieveAUActivityAssignee')}

                {renderItem('CanRecieveEmailNotificationNewAssigneeActivity', 'CanRecieveAUNewAssigneeActivity', 'manage_profile.notification_setting.activity_new_assignee')}

                {isModuleEnabled('manage_profile.notification_setting.activity_watcher') &&
                    renderItem('CanRecieveEmailNotificationActivityWatcher', 'CanRecieveAUActivityWatcher')
                }

                {uView?.permissions?.can_receive_un_assigned_activities &&
                    renderItem('CanRecieveEmailNotificationNewAssigneeOrganizationActivity', 'CanRecieveAUNewAssigneeOrganizationActivity')
                }
                {isModuleEnabled('manage_profile.notification_setting.diary_notifications') &&
                    <>
                        <strong>Diary Notifications</strong>
                        {renderItem('CanRecieveEmailNotificationMadeTodoOwner', 'CanRecieveAUMadeTodoOwner')}

                        {renderItem('CanRecieveEmailNotificationEditTodo', 'CanRecieveAUEditTodo')}

                        {renderItem('CanRecieveEmailNotificationTodoReminder', 'CanRecieveAUTodoReminder')}
                    </>
                }

            </div>
        </React.Fragment>
    )
};

export default ManageNotifications;